import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react'
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  SupportedLanguage,
} from '@/i18n'

interface LanguageContextType {
  language: SupportedLanguage
  changeLanguage: (lang: SupportedLanguage) => void
}

const LanguageContext = createContext<Maybe<LanguageContextType>>(undefined)

export function LanguageProvider() {
  const { i18n } = useTranslation()
  const { lang } = useParams<{ lang: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isInitialMount = useRef(true)

  // Get current language from URL or use default
  const currentLanguage = (
    SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)
      ? lang
      : DEFAULT_LANGUAGE
  ) as SupportedLanguage

  // Validate language and redirect if invalid
  useEffect(() => {
    if (lang && !SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
      const pathWithoutLang = location.pathname.replace(/^\/[^/]+/, '')
      navigate(`/${DEFAULT_LANGUAGE}${pathWithoutLang}`, { replace: true })
    }
  }, [lang, location.pathname, navigate])

  // Sync i18n language with URL parameter
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage)
    }
    // Update HTML lang attribute for proper font rendering
    document.documentElement.lang = currentLanguage
    isInitialMount.current = false
  }, [currentLanguage, i18n])

  // Change language and update URL
  const changeLanguage = useCallback(
    (newLang: SupportedLanguage) => {
      if (!SUPPORTED_LANGUAGES.includes(newLang)) {
        return
      }

      // Prevent navigation if already on the same language
      if (newLang === currentLanguage) {
        return
      }

      // Get current path without language prefix
      const pathWithoutLang = location.pathname.replace(/^\/(en|zh)/, '')

      // Navigate to new path with language prefix
      navigate(
        `/${newLang}${pathWithoutLang}${location.search}${location.hash}`,
        {
          replace: true,
        }
      )
    },
    [
      currentLanguage,
      location.pathname,
      location.search,
      location.hash,
      navigate,
    ]
  )

  const contextValue = useMemo<LanguageContextType>(
    () => ({
      language: currentLanguage,
      changeLanguage,
    }),
    [currentLanguage, changeLanguage]
  )

  return (
    <LanguageContext.Provider value={contextValue}>
      <Outlet />
    </LanguageContext.Provider>
  )
}

// Custom hook to use language context
// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
